import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';

interface BreakdownItem {
  _count: Record<string, number>;
  status?: string;
  onboardingStatus?: string;
  assetType?: string;
  operationalStatus?: string;
}

interface AdminStats {
  totals: {
    tenants?: number;
    users?: number;
    systemAdmins?: number;
    tenantUsers?: number;
    counterparties?: number;
    assets?: number;
    contracts?: number;
    documents?: number;
  };
  onboarding: {
    completed?: number;
    inProgress?: number;
    draftCounterparties?: number;
  };
  assets: {
    active?: number;
  };
  breakdowns: {
    counterpartiesByStatus?: BreakdownItem[];
    counterpartiesByOnboardingStatus?: BreakdownItem[];
    assetsByType?: BreakdownItem[];
    assetsByOperationalStatus?: BreakdownItem[];
  };
}

interface KpiDef {
  key: string;
  label: string;
  icon: string;
  getValue: (s: AdminStats) => number;
}

const KPIS: KpiDef[] = [
  { key: 'tenants', label: 'Total Tenants', icon: 'ri-server-line', getValue: (s) => s.totals?.tenants ?? 0 },
  { key: 'users', label: 'Total Users', icon: 'ri-team-line', getValue: (s) => s.totals?.users ?? 0 },
  { key: 'systemAdmins', label: 'System Admins', icon: 'ri-shield-user-line', getValue: (s) => s.totals?.systemAdmins ?? 0 },
  { key: 'tenantUsers', label: 'Tenant Users', icon: 'ri-user-line', getValue: (s) => s.totals?.tenantUsers ?? 0 },
  { key: 'counterparties', label: 'Counterparties', icon: 'ri-building-2-line', getValue: (s) => s.totals?.counterparties ?? 0 },
  { key: 'assets', label: 'Assets', icon: 'ri-flashlight-line', getValue: (s) => s.totals?.assets ?? 0 },
  { key: 'contracts', label: 'Contracts', icon: 'ri-file-list-3-line', getValue: (s) => s.totals?.contracts ?? 0 },
  { key: 'documents', label: 'Documents', icon: 'ri-folder-line', getValue: (s) => s.totals?.documents ?? 0 },
  { key: 'completedOnboardings', label: 'Completed Onboardings', icon: 'ri-checkbox-circle-line', getValue: (s) => s.onboarding?.completed ?? 0 },
  { key: 'inProgressOnboardings', label: 'In Progress Onboardings', icon: 'ri-time-line', getValue: (s) => s.onboarding?.inProgress ?? 0 },
  { key: 'draftCounterparties', label: 'Draft Counterparties', icon: 'ri-draft-line', getValue: (s) => s.onboarding?.draftCounterparties ?? 0 },
  { key: 'activeAssets', label: 'Active Assets', icon: 'ri-battery-charge-line', getValue: (s) => s.assets?.active ?? 0 },
];

const BREAKDOWN_COLORS = [
  '#D12229', '#1F5B8F', '#0D7A3C', '#B8860B', '#6B6B70',
  '#8B4513', '#4A4A50', '#2D5A7B',
];

function BreakdownBar({ data, title, isDark }: { data: Record<string, number>; title: string; isDark: boolean }) {
  if (!data || Object.keys(data).length === 0) return null;

  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const barTrack = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const separator = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';

  const numericEntries = Object.entries(data).map(([k, v]) => [k, Number(v)] as [string, number]);
  const total = numericEntries.reduce((a, [, b]) => a + b, 0);
  const entries = numericEntries.sort((a, b) => b[1] - a[1]);

  return (
    <div className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      <h3 className="text-sm font-semibold mb-4" style={{ color: textMain }}>{title}</h3>
      <div className="space-y-3">
        {entries.map(([label, valueNum], i) => {
          const pct = total > 0 ? (valueNum / total) * 100 : 0;
          return (
            <div key={label}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="font-medium capitalize" style={{ color: textMain }}>{label}</span>
                <span style={{ color: textMuted }}>{valueNum}</span>
              </div>
              <div className="w-full h-2 rounded-full" style={{ background: barTrack }}>
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: BREAKDOWN_COLORS[i % BREAKDOWN_COLORS.length] }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 pt-3 text-xs font-medium text-right" style={{ color: textMuted, borderTop: `1px solid ${separator}` }}>
        Total: {total}
      </div>
    </div>
  );
}

function toBreakdownRecord(items?: BreakdownItem[]): Record<string, number> | undefined {
  if (!items || items.length === 0) return undefined;
  const record: Record<string, number> = {};
  for (const item of items) {
    const label = item.status ?? item.onboardingStatus ?? item.assetType ?? item.operationalStatus ?? 'unknown';
    const countKey = Object.keys(item._count)[0] ?? '';
    const value = Number(item._count?.[countKey] ?? 0);
    record[label] = value;
  }
  return record;
}

export default function PlatformDashboardPage() {
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const iconBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<AdminStats>('/admin/stats');
      if (!data || typeof data !== 'object') { setError('Invalid response'); setStats(null); }
      else { setStats(data); }
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Failed to load admin stats');
      setError(msg);
      if (!(err instanceof ApiError && err.status === 404)) addToast(msg, 'error');
    } finally { setLoading(false); }
  };

  return (
    <DashboardLayout breadcrumb={['Platform Admin', 'Dashboard']}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>Platform Dashboard</h1>
          <p className="text-sm mt-1" style={{ color: textMuted }}>System-wide overview and key metrics</p>
        </div>
        <button onClick={loadStats} className="w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-colors" style={{ border: `1px solid ${cardBorder}`, background: cardBg }} title="Refresh">
          <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`} style={{ color: textMuted }}></i>
        </button>
      </div>

      {loading && (
        <div className="p-12 text-center rounded-xl" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
          <i className="ri-loader-4-line animate-spin text-2xl" style={{ color: textMuted }}></i>
          <p className="text-sm mt-3" style={{ color: textMuted }}>Loading system stats...</p>
        </div>
      )}

      {!loading && error && (
        <div className="p-12 text-center rounded-xl" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}>
            <i className="ri-error-warning-line text-xl" style={{ color: '#D12229' }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: textMain }}>Unable to load stats</p>
          <p className="text-xs mt-1" style={{ color: textMuted }}>{error}</p>
          <button onClick={loadStats} className="mt-4 px-4 py-2 text-xs font-medium rounded-md cursor-pointer" style={{ background: '#D12229', color: '#fff' }}>Try Again</button>
        </div>
      )}

      {!loading && !error && !stats && (
        <div className="p-12 text-center rounded-xl" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}>
            <i className="ri-bar-chart-box-line text-xl" style={{ color: textMuted }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: textMain }}>No stats available</p>
          <p className="text-xs mt-1" style={{ color: textMuted }}>The server returned empty data</p>
        </div>
      )}

      {!loading && !error && stats && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-6">
            {KPIS.map((kpi) => {
              const value = kpi.getValue(stats);
              return (
                <div key={kpi.key} className="rounded-xl p-4" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: 'rgba(209,34,41,0.08)' }}>
                      <i className={`${kpi.icon} text-sm`} style={{ color: '#D12229' }}></i>
                    </div>
                    <span className="text-[11px] font-medium uppercase tracking-wider leading-tight" style={{ color: textMuted }}>{kpi.label}</span>
                  </div>
                  <div className="text-2xl font-bold" style={{ color: textMain }}>{value}</div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <BreakdownBar data={toBreakdownRecord(stats.breakdowns?.counterpartiesByStatus)} title="Counterparties by Status" isDark={isDark} />
            <BreakdownBar data={toBreakdownRecord(stats.breakdowns?.counterpartiesByOnboardingStatus)} title="Counterparties by Onboarding Status" isDark={isDark} />
            <BreakdownBar data={toBreakdownRecord(stats.breakdowns?.assetsByType)} title="Assets by Type" isDark={isDark} />
            <BreakdownBar data={toBreakdownRecord(stats.breakdowns?.assetsByOperationalStatus)} title="Assets by Operational Status" isDark={isDark} />
          </div>
        </>
      )}
    </DashboardLayout>
  );
}